/**
 * ModifiableFactorsPanel.jsx — iter880
 *
 * "Модифицируеми фактори" панел в BHI. Показва 6 evidence-based фактора
 * (BMI, активни минути, стъпки, сън, алкохол, пушене), всеки със статус
 * green/amber/red/unknown и конкретно действие.
 *
 * Няма композитен риск-скор. Всеки фактор стои сам.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Watch, Loader2, RefreshCw, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  green:   { icon: CheckCircle2, ring: "border-emerald-300 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10", iconCls: "text-emerald-600", badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200", label: "В цел" },
  amber:   { icon: AlertTriangle, ring: "border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10", iconCls: "text-amber-600", badge: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200", label: "Близо до целта" },
  red:     { icon: XCircle, ring: "border-rose-300 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10", iconCls: "text-rose-600", badge: "bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200", label: "Далеч от целта" },
  unknown: { icon: HelpCircle, ring: "border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5", iconCls: "text-slate-400", badge: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200", label: "Липсват данни" },
};

function FactorCard({ factor }) {
  const s = STATUS_STYLES[factor.status] || STATUS_STYLES.unknown;
  const Icon = s.icon;
  const displayValue = factor.value === null || factor.value === undefined
    ? "—"
    : (typeof factor.value === "number" ? factor.value : String(factor.value));

  return (
    <article
      className={`rounded-xl border ${s.ring} p-4`}
      data-testid={`mf-card-${factor.key}`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{factor.label_bg}</h3>
          <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
            Цел: {factor.target_bg}
          </p>
        </div>
        <Icon className={`h-5 w-5 shrink-0 ${s.iconCls}`} data-testid={`mf-status-${factor.key}`} />
      </div>

      <div className="mb-2 flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white" data-testid={`mf-value-${factor.key}`}>
          {displayValue}
        </span>
        {factor.unit && <span className="text-xs text-slate-500">{factor.unit}</span>}
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.badge}`}>
          {s.label}
        </span>
      </div>

      <p className="mb-2 text-xs leading-relaxed text-slate-700 dark:text-slate-200">
        {factor.action_bg}
      </p>

      <p className="text-[10px] text-slate-400 dark:text-slate-500">
        <em>Референт:</em> {factor.citation}
      </p>
    </article>
  );
}

export default function ModifiableFactorsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  const fetchFactors = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/bhi/modifiable-factors`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Грешка при зареждане");
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async () => {
    setSyncLoading(true);
    try {
      await axios.post(`${API}/wearables/google/sync`);
      await fetchFactors();
    } catch (e) {
      // Silent — panel will just keep the "unknown" state for wearables factors.
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    fetchFactors();
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900/40" data-testid="mf-loading">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Зареждане на факторите…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200" data-testid="mf-error">
        {error}
      </div>
    );
  }

  const factors = data?.factors || [];
  const showConnectCTA = !data?.google_health_connected;
  const wearableFactorsUnknown = factors
    .filter(f => ["steps_day", "active_minutes_week", "sleep_hours_night"].includes(f.key))
    .every(f => f.status === "unknown");

  return (
    <div className="space-y-4" data-testid="modifiable-factors-panel">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4 dark:border-white/10 dark:from-slate-900 dark:to-slate-900/50">
        <div>
          <h2 className="text-lg font-bold" data-testid="mf-summary-title">Фактори, върху които можеш да въздействаш</h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            6 evidence-based фактора · всеки със собствена действена препоръка
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 font-bold text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200" data-testid="mf-count-green">
            <CheckCircle2 className="h-3 w-3" /> {data?.green_count ?? 0} в цел
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-200" data-testid="mf-count-amber">
            <AlertTriangle className="h-3 w-3" /> {data?.amber_count ?? 0} близо
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 font-bold text-rose-800 dark:bg-rose-500/20 dark:text-rose-200" data-testid="mf-count-red">
            <XCircle className="h-3 w-3" /> {data?.red_count ?? 0} далеч
          </span>
          {data?.unknown_count > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-1 font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200" data-testid="mf-count-unknown">
              <HelpCircle className="h-3 w-3" /> {data?.unknown_count ?? 0} липсват
            </span>
          )}
        </div>
      </div>

      {/* Connect Google Health CTA (only if no active connection) */}
      {showConnectCTA && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 dark:border-indigo-500/30 dark:bg-indigo-500/10" data-testid="mf-connect-cta">
          <div className="flex items-start gap-3">
            <Watch className="h-5 w-5 shrink-0 text-indigo-600" />
            <div className="flex-1">
              <div className="text-sm font-bold text-indigo-900 dark:text-indigo-100">Свържете Google Health</div>
              <p className="mt-1 text-xs text-indigo-800 dark:text-indigo-200">
                3 от факторите (стъпки, активни минути, сън) идват от Google Health. Без свързване тези данни ще липсват.
              </p>
              <Link
                to="/settings/wearables"
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white shadow hover:bg-indigo-700"
                data-testid="mf-connect-link"
              >
                Отвори настройките <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Manual re-sync (only if connected and wearable factors known or unknown) */}
      {!showConnectCTA && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs text-slate-500 dark:border-white/10 dark:bg-slate-900/40">
          <span>
            Последна Google Health синхронизация:{" "}
            <strong>{data?.google_health_last_sync
              ? new Date(data.google_health_last_sync).toLocaleString("bg-BG", { dateStyle: "medium", timeStyle: "short" })
              : "никога"}</strong>
            {wearableFactorsUnknown && " · липсват данни за стъпки/сън"}
          </span>
          <button
            onClick={triggerSync}
            disabled={syncLoading}
            className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 font-semibold hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:hover:bg-slate-700"
            data-testid="mf-sync-btn"
          >
            {syncLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Синхронизирай
          </button>
        </div>
      )}

      {/* Factor grid */}
      <div className="grid gap-3 md:grid-cols-2" data-testid="mf-grid">
        {factors.map(f => <FactorCard key={f.key} factor={f} />)}
      </div>
    </div>
  );
}
